import type { GetStaticProps } from 'next';
import type { ISbStoryData } from 'storyblok';

import { ssgClient } from '../apollo/api/avantArteClient';
import Layout from '../components/Layout';
import { Estates } from '../components/PageTypes/Estates';
import { AvantArteArtistsSearchGateway } from '../gateways/AvantArteArtistsSearchGateway';
import { imageOgStaticLoader } from '../helpers/og-image';
import type { AugmentedArtistCard } from '../types/Artists';
import {
  ArtistStoryblok,
  EstatesStoryblok,
} from '../types/generated-storyblok';
import { augmentArtists } from '../utils/artists';
import { getStoryblokPage, StoryResponse } from '../utils/storyblok';
import { pageTranslations } from '../utils/translations';

type EstatesPageProps = {
  locale: string;
  locales: string[];
  story: EstatesStoryblok;
  augmentedArtists: AugmentedArtistCard[];
  ogImageUrl: string;
};

const Page = ({
  augmentedArtists,
  locale,
  locales,
  ogImageUrl,
  story,
}: EstatesPageProps) => {
  return (
    <Layout
      description={story.seo.description}
      locale={locale}
      locales={locales}
      ogImageUrl={ogImageUrl}
      title={story.seo.title}
    >
      <Estates story={story} augmentedArtists={augmentedArtists} />
    </Layout>
  );
};

export const getStaticProps: GetStaticProps = async (ctx) => {
  const { locale = 'en', locales, preview = false } = ctx;

  const page: StoryResponse<{ content: EstatesStoryblok }> =
    (await getStoryblokPage('cdn/stories/estates', {
      locale,
      preview,
      resolve_links: 'story',
      resolve_relations: [
        'Estates.section_1_release_pages',
        'Estates.section_4_artist_pages',
        'Estates.section_5_insight_pages',
      ],
    })) as StoryResponse<{ content: EstatesStoryblok }>;

  if (!page) return { notFound: true };

  const resolvedArtists = page.data.story.content.section_4_artist_pages.filter(
    (item): item is ISbStoryData<ArtistStoryblok> => typeof item !== 'string',
  );

  const mappedArtists: ArtistCard[] = resolvedArtists.map((artist) => ({
    uuid: artist.uuid,
    alt: artist.content.thumbnail?.alt || '',
    focus: artist.content.thumbnail?.focus || '',
    image: artist.content.thumbnail?.filename || '',
    hideCollaborations: artist.content.hideCollaborations || null,
    link: artist.full_slug || '',
    name: artist.content.name || '',
    overline: '',
    createdAt: artist.created_at || '',
    firstPublishedAt: artist.first_published_at || '',
    galleryImages: [],
    slug: artist.slug,
    plannedReleases:
      (artist.content.plannedReleases as PlannedRelease[] | undefined) ?? null,
    isEstate: artist.content.isEstate ?? false,
  }));

  const internalArtists = await new AvantArteArtistsSearchGateway(
    ssgClient(ctx),
  ).getArtistsBySlug(mappedArtists.map((a) => a.slug));

  const augmentedArtists = await augmentArtists(mappedArtists, internalArtists);

  const ogImageUrl = await imageOgStaticLoader({
    titleKey: page.data.story.content.seo.title,
    imageSrc: 'https://images.avantarte.org/media/3Cnb82UxNqvYwD8DDgSYXmv1REC',
    imageWidth: 1200,
    imageHeight: 630,
    overlay: true,
    locale,
  });

  return {
    props: {
      story: page.data.story.content,
      augmentedArtists,
      preview,
      locale,
      locales,
      ogImageUrl,
      ...(await pageTranslations(locale, ['artists', 'og'])),
    },
  };
};

export default Page;
